import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Menu,
  X,
  BookOpen,
  GraduationCap,
  ClipboardCheck,
  Microscope,
  Settings,
  User,
  Home,
  ChevronDown,
  LogIn,
  LogOut,
  Brain,
  Shield,
  RadioTower
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import UserProfile from '../auth/UserProfile'
import GoogleSignInButton from '../auth/GoogleSignInButton'
import NotificationBell from './NotificationBell'

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileUserMenuOpen, setIsMobileUserMenuOpen] = useState(false)
  const [hoveredTab, setHoveredTab] = useState<string | null>(null)
  const [underlineStyle, setUnderlineStyle] = useState({ width: 0, left: 0 })

  const navRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const { isAuthenticated, isLoading, user, logout } = useAuth()
  const { isTeacher, isAdmin } = usePermissions()

  // Menu chính gọn 5 mục — "Khoá học" = CATALOG công khai (ai cũng xem & ghi danh được).
  // "Đang học" + "Quản lý khoá" chuyển vào trang Profile; Flashcard nằm ở Trang chủ (sau Tiếng Anh Vật lí).
  const navigation = [
    { name: 'Trang chủ', href: '/', icon: Home },
    { name: 'Khoá học', href: '/khoa-hoc', icon: BookOpen },
    { name: 'Thi Online', href: '/trac-nghiem', icon: ClipboardCheck },
    { name: 'Mô phỏng', href: '/mo-phong', icon: Microscope },
    { name: 'Nhà bác học', href: '/nha-bac-hoc', icon: GraduationCap },
    ...(import.meta.env.VITE_CHALKBRIDGE_ENABLED === 'true' && isTeacher
      ? [{ name: 'ChalkBridge', href: '/chalkbridge/teacher', icon: RadioTower }]
      : []),
  ]

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  // Hàm xử lý hover để tính toán vị trí đường gạch chân
  const handleTabHover = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!navRef.current) return

    const tabElement = event.currentTarget
    const navRect = navRef.current.getBoundingClientRect()
    const tabRect = tabElement.getBoundingClientRect()

    const left = tabRect.left - navRect.left
    const width = tabRect.width

    setHoveredTab(href)
    setUnderlineStyle({ width, left })
  }

  const handleTabLeave = () => {
    setHoveredTab(null)
    setUnderlineStyle({ width: 0, left: 0 })
  }

  return (
    <nav className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-50 shadow-sm transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <img
                  src="/AI-PhysicsBook.png"
                  alt="AI-PhysicsBook"
                  className="w-10 h-10 object-contain transition-transform group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-brilliant rounded-full opacity-0 group-hover:opacity-20 transition-opacity" />
              </div>
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  AI-PhysicsBook
                </span>
                <div className="text-xs text-dark-500 -mt-1">Learn Smarter with AI Interactive Ebook</div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div ref={navRef} className="ml-10 flex items-baseline space-x-1 relative">
              {/* Animated underline - Đường gạch chân chuyển động */}
              <div
                className="absolute bottom-0 h-1 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-300 ease-in-out shadow-lg"
                style={{
                  width: `${underlineStyle.width}px`,
                  left: `${underlineStyle.left}px`,
                  opacity: hoveredTab ? 1 : 0
                }}
              />

              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onMouseEnter={(e) => handleTabHover(e, item.href)}
                  onMouseLeave={handleTabLeave}
                  className={`group relative px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ease-in-out ${isActive(item.href)
                    ? 'text-primary-800 bg-primary-200 shadow-lg'
                    : 'text-dark-600 hover:text-primary-700 hover:bg-primary-100 hover:shadow-md'
                    }`}
                >
                  <div className="flex items-center space-x-2 relative z-10">
                    <item.icon className={`w-4 h-4 transition-all duration-300 group-hover:scale-110 ${isActive(item.href) ? 'text-primary-800' : 'text-current group-hover:text-primary-700'
                      }`} />
                    <span className="transition-colors duration-300 font-semibold">{item.name}</span>
                  </div>

                  {/* Subtle background glow on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-100/0 via-primary-100/40 to-primary-100/0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>
              ))}
            </div>
          </div>

          {/* Right side buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Chuông thông báo (chỉ hiện khi đã đăng nhập) */}
            <NotificationBell />
            {/* Google Sign-In Button */}
            <GoogleSignInButton />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-1">
            <NotificationBell />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-xl text-dark-600 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200"
            >
              {isOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white/80 backdrop-blur-xl border-t border-white/20 shadow-xl">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group relative block px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ease-in-out ${isActive(item.href)
                  ? 'text-primary-800 bg-primary-200 shadow-lg'
                  : 'text-dark-600 hover:text-primary-700 hover:bg-primary-100'
                  }`}
                onClick={() => setIsOpen(false)}
              >
                <div className="flex items-center space-x-3 relative z-10">
                  <item.icon className={`w-5 h-5 transition-all duration-300 group-hover:scale-110 ${isActive(item.href) ? 'text-primary-800' : 'text-current group-hover:text-primary-700'
                    }`} />
                  <span className="transition-colors duration-300">{item.name}</span>
                </div>

                {/* Animated underline for mobile - Đường gạch chân cam đậm */}
                <div className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-300 ease-in-out shadow-lg ${isActive(item.href)
                  ? 'w-full opacity-100'
                  : 'w-0 opacity-0 group-hover:w-full group-hover:opacity-100'
                  }`} />

                {/* Subtle background glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary-50/0 via-primary-50/20 to-primary-50/0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
            ))}

            {/* Mobile user menu - Thu gọn */}
            <hr className="my-3 border-light-200" />

            {isAuthenticated && user ? (
              <div className="px-4 py-2">
                {/* User Account Button */}
                <button
                  onClick={() => setIsMobileUserMenuOpen(!isMobileUserMenuOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium text-dark-600 hover:bg-primary-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-md">
                      <span className="text-white font-bold text-lg">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">Tài khoản</p>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 transition-transform ${isMobileUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {isMobileUserMenuOpen && (
                  <div className="mt-2 space-y-1 pl-4">
                    <Link
                      to="/ho-so"
                      className="block px-4 py-2.5 rounded-lg text-sm font-medium text-dark-600 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                      onClick={() => {
                        setIsOpen(false)
                        setIsMobileUserMenuOpen(false)
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <User className="w-4 h-4" />
                        <span>Hồ sơ</span>
                      </div>
                    </Link>
                    <Link
                      to="/cai-dat"
                      className="block px-4 py-2.5 rounded-lg text-sm font-medium text-dark-600 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                      onClick={() => {
                        setIsOpen(false)
                        setIsMobileUserMenuOpen(false)
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <Settings className="w-4 h-4" />
                        <span>Cài đặt</span>
                      </div>
                    </Link>

                    {/* Admin Menu - Only show for admins */}
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="block px-4 py-2.5 rounded-lg text-sm font-medium text-dark-600 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                        onClick={() => {
                          setIsOpen(false)
                          setIsMobileUserMenuOpen(false)
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <Shield className="w-4 h-4" />
                          <span>Quản trị</span>
                        </div>
                      </Link>
                    )}

                    <button
                      onClick={async () => {
                        try {
                          await logout()
                          setIsOpen(false)
                          setIsMobileUserMenuOpen(false)
                        } catch (error) {
                          console.error('Logout error:', error)
                        }
                      }}
                      className="w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <LogOut className="w-4 h-4" />
                        <span>Đăng xuất</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="px-4 py-3">
                <GoogleSignInButton />
              </div>
            )}
          </div>
        </div>
      )}


    </nav>
  )
}

export default Navigation
